import { useRef } from 'react';

import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { QUESTION_TYPES } from '@api/instant_survey';

import Question from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/components/Question';
import SequentialMonadicBlock from '../../pages/CreateInstantSurvey/components/Questionnaire/components/QuestionnaireList/components/SequentialMonadicBlock';
import { EMPTY_PIPING_QUESTIONS_MAP, getQuestionKey, noop } from './utilities/helpers';
import type {
  DragEndHandler,
  PreviewHandler,
  QuestionHandler,
  QuestionnaireDisplayProps,
  SetQuestionLogic,
} from './utilities/types';

import styles from './_index.module.scss';

const QuestionnaireDisplay = (props: QuestionnaireDisplayProps) => {
  const {
    canPreviewSurvey = false,
    firstErrorRef,
    isDetailedQuestionView = true,
    isPreviewDisabled = false,
    previewDisabledTooltip,
    questionDisplayIndexById,
    questionLogic = {},
    questions,
    selectedQuestions,
  } = props;

  const fallbackFirstErrorRef = useRef<HTMLDivElement | null>(null);
  const isEditMode = props.mode === 'edit';
  const resolvedFirstErrorRef = firstErrorRef || fallbackFirstErrorRef;
  const resolvedSelectedQuestions = selectedQuestions || questions;

  const onDragEnd: DragEndHandler = isEditMode ? props.onDragEnd : noop;
  const onDuplicateQuestion: QuestionHandler = isEditMode ? props.onDuplicateQuestion : noop;
  const onEditQuestion: QuestionHandler = isEditMode ? props.onEditQuestion : noop;
  const onPreviewSurvey: PreviewHandler = props.onPreviewSurvey || noop;
  const onRemoveQuestion: QuestionHandler = isEditMode ? props.onRemoveQuestion : noop;
  const pipingQuestionsMap = isEditMode ? props.pipingQuestionsMap : EMPTY_PIPING_QUESTIONS_MAP;
  const setQuestionLogic: SetQuestionLogic = isEditMode ? props.setQuestionLogic : noop;
  const showActions = isEditMode ? props.showActions : false;

  if (questions.length === 0) return null;

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {(provided) => (
          <div
            className={styles['questionnaire-display-selections']}
            {...provided.droppableProps}
            ref={provided.innerRef}
          >
            {questions.map((question, index) => (
              <Draggable
                draggableId={getQuestionKey(question, index)}
                index={index}
                isDragDisabled={!isEditMode}
                key={getQuestionKey(question, index)}
              >
                {(provided) => {
                  return question.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ? (
                    <Question
                      canPreviewSurvey={canPreviewSurvey}
                      firstErrorRef={resolvedFirstErrorRef}
                      index={index}
                      isDetailedQuestionView={isDetailedQuestionView}
                      isPreviewDisabled={isPreviewDisabled}
                      key={getQuestionKey(question, index)}
                      number={questionDisplayIndexById[question.id] ?? String(index + 1)}
                      onDuplicateQuestion={onDuplicateQuestion}
                      onEditQuestion={onEditQuestion}
                      onPreviewSurvey={onPreviewSurvey}
                      onRemoveQuestion={onRemoveQuestion}
                      previewDisabledTooltip={previewDisabledTooltip}
                      provided={provided}
                      question={question}
                      questionLogic={questionLogic}
                      selectedQuestions={resolvedSelectedQuestions}
                      setQuestionLogic={setQuestionLogic}
                      showActions={showActions}
                    />
                  ) : (
                    <SequentialMonadicBlock
                      canPreviewSurvey={canPreviewSurvey}
                      firstErrorRef={resolvedFirstErrorRef}
                      isDetailedQuestionView={isDetailedQuestionView}
                      isPreviewDisabled={isPreviewDisabled}
                      onPreviewSurvey={onPreviewSurvey}
                      onRemoveQuestion={onRemoveQuestion}
                      pipingQuestionsMap={pipingQuestionsMap}
                      previewDisabledTooltip={previewDisabledTooltip}
                      provided={provided}
                      question={question}
                      questionLogic={questionLogic}
                      selectedQuestions={resolvedSelectedQuestions}
                      setQuestionLogic={setQuestionLogic}
                      showActions={showActions}
                    />
                  );
                }}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default QuestionnaireDisplay;
